import { FC, ReactNode, MouseEventHandler } from 'react';

import { IoCloseCircleOutline, IoDownload, IoEye } from 'react-icons/io5';
import { cn, convertToMegabytes } from 'utils/helpers';
import Tooltip from 'ui/components/common/Tooltip';
import { SIZE } from 'utils/constants';
import { cva } from 'class-variance-authority';

const fileContainerClassNames = cva(
  [
    'flex justify-between items-center',
    'border-[0.5px] border-separator-primary',
    'rounded-[6px]',
    'bg-fill-primary hover:bg-fill-secondary',
    '',
  ],
  {
    variants: {
      size: {
        [SIZE.Small]: ['px-2 py-[6px] h-10'],
        [SIZE.Large]: ['px-3 py-2 h-[52px]'],
      },
    },
    defaultVariants: {
      size: SIZE.Small,
    },
  },
);

interface File {
  fileName: string;
  fileSize: number;
  fileType: string;
}

interface UploadListItemProps {
  file: File;
  size?: SIZE.Small | SIZE.Large;
  icon?: ReactNode;
  showDownload?: boolean;
  showSize?: boolean;
  showPreview?: boolean;
  showRemove?: boolean;
  onRemove?: MouseEventHandler<SVGElement>;
  onDownload?: MouseEventHandler<SVGElement>;
  isUploaded?: boolean;
}

const UploadListItem: FC<UploadListItemProps> = ({
  file,
  size,
  icon,
  showDownload = false,
  showSize = false,
  showPreview = false,
  showRemove = true,
  onRemove = () => {},
  onDownload = () => {},
}) => (
  <div className={cn(fileContainerClassNames({ size }))}>
    <div className="flex items-center w-2/3">
      <div
        className={cn(icon ? 'block' : 'hidden', 'rounded-[6px] bg-[#ADC8DB]')}
      >
        {icon}
      </div>
      <div
        className={cn(
          size === SIZE.Small ? 'text-xs' : 'text-sm',
          'text-secondary truncate',
        )}
      >
        {file.fileName}
      </div>
    </div>
    <div className="flex gap-[14px] items-center">
      <div
        className={cn(
          (!file.fileSize || !showSize) && 'hidden',
          size === SIZE.Small ? 'text-[10px]' : 'text-xs',
          'text-content',
        )}
      >
        {convertToMegabytes(file.fileSize)}
      </div>
      <div className={cn('flex', size === SIZE.Small ? 'gap-[10px]' : 'gap-4')}>
        <IoEye
          className={cn(
            showPreview ? 'block' : 'hidden',
            size === SIZE.Small ? 'w-[14px] h-[14px]' : 'w-[18px] h-[18px]',
            'text-content',
          )}
        />
        {showDownload && (
          <Tooltip title="Download Attachment">
            <IoDownload
              role="button"
              onClick={onDownload}
              className={cn(
                size === SIZE.Small ? 'h-[14px] w-[14px]' : 'h-[18px] w-[18px]',
                'text-content',
              )}
            />
          </Tooltip>
        )}
        <Tooltip title="Remove Attachment" placement="right">
          <IoCloseCircleOutline
            role="button"
            onClick={onRemove}
            className={cn(
              showRemove ? 'block' : 'hidden',
              size === SIZE.Small ? 'w-[14px] h-[14px]' : 'w-[18px] h-[18px]',
              'text-content',
            )}
          />
        </Tooltip>
      </div>
    </div>
  </div>
);

export default UploadListItem;
